import { useMemo } from 'react';
import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import type { LabelModel } from '@hotjar/labels';
import { useBoundingBox } from '@hotjar/webapp/hooks/useBoundingBox';
import type { ElementLike } from '@hotjar/webapp/hooks/useBoundingBox';
import type { RecordingHighlight } from '@hotjar/webapp/domains/highlights/model';
import { useLollipopInteraction } from '@hotjar/webapp/features/Playbacks/Timeline/Lollipops/useLollipopInteraction';
import { HighlightIndicator } from '@hotjar/webapp/features/Highlights/Heatmaps/HighlightIndicator';
import type { ItemType, TimelineActiveItem } from '@hotjar/webapp/features/Playbacks/types';

import { Lollipop, VERTICAL_LINE_SIZE } from './Lollipop';

const StyledLollipop = styled(Lollipop)`
  z-index: 2;
  span {
    z-index: 2;
  }
  :hover {
    z-index: 2;
  }
`;

const THREAD_EDGE_OFFSET = 20;

const Popover = styled.span<{ horizontalOffset: number; isParentWithStem: boolean }>`
  bottom: ${(props) => (props.isParentWithStem ? 0 : 45)}px;
  position: absolute;
  transform: translateX(calc(-50% + ${(props) => props.horizontalOffset}px));
  cursor: default;

  &.newItem {
    bottom: ${(props) => (props.isParentWithStem ? 0 : VERTICAL_LINE_SIZE)}px;
  }
`;

type ItemId = number | null;

interface RenderExistingContentProps {
  deactivateCurrentItem: () => void;
  deactivateNotInteractedCurrentItem: () => void;
  markCurrentItemAsInteracted: () => void;
}

interface LollipopsProps {
  activeItemId: ItemId | null;
  timelineBox: { width: number } | null;
  newItem: {
    position: { marker: number };
    time: { marker: number };
    type?: ItemType;
  } | null;
  setActiveItem: (item: TimelineActiveItem | null) => void;
  items?: LollipopHighlightItem[];
  showLollipopFooter?: boolean;
  showStickerLabels?: boolean;
  renderNewContent: (type?: ItemType) => ReactElement;
  renderExistingContent: (
    item: LollipopHighlightItem,
    props: RenderExistingContentProps,
  ) => ReactElement;
}

export interface LollipopHighlightItem {
  id: number;
  position: number;
  commentCount?: number;
  author: RecordingHighlight['author'];
  labels: RecordingHighlight['labels'];
}

type Item = {
  id: ItemId;
};

interface LollipopLegacyProps {
  onClick: (item: Item) => void;
  onMouseEnter: (item: Item) => void;
  author: {
    avatar: string;
    name: string;
  };
  hasComments?: boolean;
  labels?: string[];
  item: {
    id: number;
    labels?: LabelModel[];
  };
  horizontalOffset: number;
  showStickerLabels?: boolean;
  setPopoverRef: (node: ElementLike | null) => void;
  renderedExistingContent: ReactElement;
  isActive: boolean;
}

interface LollipopPersistentLabelProps extends LollipopLegacyProps {
  isInteractionHappening?: boolean;
}

const LollipopLegacy = ({
  item,
  isActive,
  horizontalOffset,
  showStickerLabels,
  setPopoverRef,
  renderedExistingContent,
  onClick,
  onMouseEnter,
  author,
  labels,
  hasComments,
}: LollipopLegacyProps): JSX.Element =>
  isActive ? (
    <Popover
      horizontalOffset={horizontalOffset}
      isParentWithStem={!showStickerLabels}
      ref={setPopoverRef}
    >
      {renderedExistingContent}
    </Popover>
  ) : (
    <HighlightIndicator
      onClick={() => onClick(item)}
      onMouseEnter={() => onMouseEnter(item)}
      showReactionIcon={showStickerLabels}
      author={author}
      labels={labels}
      hasComments={hasComments}
    />
  );

const LollipopWithPersistentLabel = ({
  item,
  isActive,
  isInteractionHappening,
  horizontalOffset,
  showStickerLabels,
  setPopoverRef,
  renderedExistingContent,
  onClick,
  onMouseEnter,
  author,
  labels,
  hasComments,
}: LollipopPersistentLabelProps): JSX.Element => {
  return (
    <>
      {isActive && (
        <Popover
          horizontalOffset={horizontalOffset}
          isParentWithStem={!showStickerLabels}
          ref={setPopoverRef}
        >
          {renderedExistingContent}
        </Popover>
      )}
      <div
        css={css`
          transition: all 0.2s ease-in-out;

          ${isActive ? `transform: scale(1.2);` : null}
          ${!isActive && isInteractionHappening ? `opacity: 0.8;` : null}
        `}
      >
        <HighlightIndicator
          onClick={() => onClick(item)}
          onMouseEnter={() => onMouseEnter(item)}
          showReactionIcon={showStickerLabels}
          author={author}
          labels={labels}
          hasComments={hasComments}
        />
      </div>
    </>
  );
};

export const Lollipops = ({
  activeItemId,
  timelineBox,
  newItem,
  setActiveItem,
  items = [],
  showLollipopFooter,
  showStickerLabels,
  renderExistingContent,
  renderNewContent,
}: LollipopsProps): JSX.Element => {
  const [popoverBoundingBox, setPopoverRef] = useBoundingBox();

  // FIXME: this is not exactly working as the bounding box is updated only when the reference (so the active item)
  // changes, but in fact the left position is updated in the meantime - because transform is taken into account
  // and we're updating it when the offset is set; the final effect is that every other hover on the same lollipop
  // is broken - but only for ones that are positioned close to the left edge
  const horizontalOffset = useMemo(() => {
    const { left, right } = popoverBoundingBox;

    if (left < 0) {
      return -left + THREAD_EDGE_OFFSET;
    }

    if (window.innerWidth - right < 0) {
      return -(right - window.innerWidth + THREAD_EDGE_OFFSET);
    }

    return 0;
  }, [popoverBoundingBox]);

  const {
    deactivateCurrentItem,
    deactivateNotInteractedCurrentItem,
    markCurrentItemAsInteracted,
    makeItemActive,
    openItemPreview,
  } = useLollipopInteraction({
    setActiveItem,
    newItemTime: newItem?.time.marker ?? null,
  });

  const timelineWidth = timelineBox?.width ?? 0;

  return (
    <>
      {items.map((item) => {
        const props = {
          item,
          isActive: Number(item.id) === activeItemId,
          isInteractionHappening: !!activeItemId,
          horizontalOffset,
          showStickerLabels,
          setPopoverRef,
          renderedExistingContent: renderExistingContent(item, {
            deactivateCurrentItem,
            deactivateNotInteractedCurrentItem,
            markCurrentItemAsInteracted,
          }),
          onClick: () => makeItemActive(item),
          onMouseEnter: () => {
            openItemPreview(item);
          },
          author: { avatar: item.author.avatar as string, name: item.author.name },
          labels: item.labels?.map((label) => label.id),
          hasComments: item?.commentCount ? item.commentCount > 0 : false,
        };

        return (
          <StyledLollipop
            increaseZIndex={Number(item.id) === activeItemId}
            key={item.id}
            position={item.position}
            showLollipopFooter={showLollipopFooter}
            showStem={!showStickerLabels}
            timelineWidth={timelineWidth}
          >
            {showStickerLabels ? (
              <LollipopWithPersistentLabel {...props} />
            ) : (
              <LollipopLegacy {...props} />
            )}
          </StyledLollipop>
        );
      })}
      {newItem !== null && renderNewContent && (
        <StyledLollipop
          increaseZIndex
          key={`new-item-${newItem.time.marker}`}
          position={newItem.position.marker}
          showLollipopFooter={showLollipopFooter}
          showStem={!showStickerLabels}
          timelineWidth={timelineWidth}
        >
          <Popover
            horizontalOffset={horizontalOffset}
            isParentWithStem={!showStickerLabels}
            ref={setPopoverRef}
            className="newItem"
          >
            {renderNewContent(newItem.type)}
          </Popover>
        </StyledLollipop>
      )}
    </>
  );
};
